import { I18n } from '@aws-amplify/core';
import { message } from 'antd';
import { NamePath } from 'antd/lib/form/interface';
import { useCallback, useState } from 'react';

import { IntegratedErgonomicToolDTO } from '@/components/ui/Inputs/types/response/customReport';
import { StressLevelDTO } from '@/components/ui/Inputs/types/response/stressLevel';
import { StepKeyToolsService } from '../../../../services/stepKeyToolsService';
import { useErgonomicToolReports } from './useErgonomicToolReports';

const stepKeyToolsService = new StepKeyToolsService();

interface UseStepKeyPopulateParams {
	integratedTools: IntegratedErgonomicToolDTO[];
	fileId: string;
	organizationId: string;
	companyId: string;
	stressLevels: StressLevelDTO[];
	stressLevelFieldPath: NamePath;
	setFieldValue: (name: NamePath, value: any) => void;
	validateFields: (nameList?: NamePath[]) => Promise<any>;
}

export function useStepKeyPopulate({
	integratedTools,
	fileId,
	organizationId,
	companyId,
	stressLevels,
	stressLevelFieldPath,
	setFieldValue,
	validateFields
}: UseStepKeyPopulateParams) {
	const [populate, setPopulate] = useState<boolean>(false);

	const { ergonomicToolMapper, disabled } = useErgonomicToolReports({
		integratedTools,
		fileId,
		organizationId,
		companyId
	});

	const handleSwitchChange = useCallback(() => {
		if (populate) {
			setPopulate(false);
			setFieldValue(stressLevelFieldPath, undefined);
			return;
		}

		const calculatedId = stepKeyToolsService.calculateStressLevelScore(stressLevels, ergonomicToolMapper);

		if (calculatedId) {
			setFieldValue(stressLevelFieldPath, calculatedId);
			validateFields([stressLevelFieldPath]);
			setPopulate(true);
			return;
		}

		message.error(I18n.get('No matching option with integrated tool score.'));
	}, [populate, stressLevels, ergonomicToolMapper, stressLevelFieldPath, setFieldValue, validateFields]);

	const resetPopulate = useCallback(() => {
		setPopulate(false);
	}, []);

	return {
		populate,
		disabled,
		ergonomicToolMapper,
		handleSwitchChange,
		resetPopulate
	};
}
